import { t } from "../../i18n";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ENGINE_VERSION } from "@tasduvar/engine";
import { useAuth } from "../../auth/useAuth";
import { useCadUi } from "../../state/cadUiStore";
import { Button } from "../../ui/Button";
import { Wordmark } from "../../ui/Wordmark";
import { NewProjectForm } from "./NewProjectForm";
import { RecentProjects } from "./RecentProjects";
import { HeroSheet } from "./HeroSheet";
import { LangSwitch } from "../../i18n/LangSwitch";
import { Showcase } from "./Showcase";
import { useHomeProjects } from "./useHomeProjects";
import { StartPage } from "./StartPage";
import { GuestHome } from "./GuestHome";

const FEATURES: [string, string][] = [
  [t("Üç duvar tipi"), t("Taş (kargir) ağırlık, betonarme konsol (T/L) ve payandalı duvar. Betonarmede TS 500 / EN 1992-1-1 / ACI 318-19 ile kesit tasarımı, en ekonomik donatı seçimi ve donatı planı.")],
  [t("Üç yönetmelik, tek girdi"), t("TBDY 2018 γR'li doğrulama, Eurocode 7 kısmi katsayı kombinasyonları (DA1/DA2/DA3) ve ABD ASD hedefleri aynı duvar üzerinde; profil değişince hedefler ve deprem katsayısı türetimi birlikte değişir.")],
  [t("Depremli hesap açık hesapla"), t("Mononobe-Okabe ile statik + dinamik itki, duvar ve kama ataleti, kh/kv doğrudan girişi; her adımın formülü ve yerine konmuş sayıları raporda. TBDY'nin kendi örneğiyle doğrulandı.")],
  [t("Kesit çiziminden rapora"), t("Ölçüleri çizim üzerinde düzenleyin, çokgen kesit çizin, 3B görünümde kontrol edin; PDF, Word ve DXF çıktısı Türkçe ya da İngilizce.")],
  [t("Metraj ve keşif"), t("Kazı, dolgu, taş/beton, kalıp, donatı (kg) ve drenaj metrajı; ÇŞB poz numaralı, düzenlenebilir birim fiyat tablosu; keşif özeti XLSX/CSV ve raporda.")],
  [t("Zemin raporundan al"), t("Zemin etüt raporunu (PDF) yükleyin; tabaka parametrelerini, taşıma gücünü, yeraltı suyunu ve deprem katsayılarını program sayfa referansıyla önerir — yapay zekâsız, tarayıcıda; siz onaylarsınız.")],
];

/**
 * Ana sayfa: giriş yapana CAD Başlangıç paftası; API'li sürümde misafire /giris kabuğunda tanıtım (uygulama yalnız üyelere — Cem 2026-09-23);
 * API'siz statik sürümde (no-api) eski vitrin + yerel proje araçları. Oturum bilinmezken boş (yanıp sönmesin).
 */
export function HomePage(): JSX.Element {
  const status = useAuth((s) => s.status);
  if (status === "unknown") return <main className="min-h-screen" />;
  return status === "authed" ? <StartPage /> : status === "guest" ? <GuestHome /> : <MarketingPage />;
}

function MarketingPage(): JSX.Element {
  const status = useAuth((s) => s.status);
  const [showForm, setShowForm] = useState(false);
  // QAT "Yeni proje" (cadUi.dialog "new") ile gelindiyse künye formu açık başlar
  useEffect(() => { if (useCadUi.getState().dialog === "new") { useCadUi.getState().setDialog(null); setShowForm(true); } }, []);
  const { local, error, fileRef, refresh, create, tryShowcase, onFile, openLocal, removeLocal } = useHomeProjects();
  return (
    <main className="min-h-screen">
      <header className="max-w-6xl mx-auto px-5 md:px-8 h-14 flex items-center justify-between gap-3">
        <Wordmark />
        <nav className="flex items-center gap-1 text-sm" aria-label={t("Hesap")}>
          {status === "guest" ? <><Link to="/giris" className="btn-ghost">{t("Giriş yap")}</Link><Link to="/kayit" className="btn-primary">{t("Hesap oluştur")}</Link></> : null}
          <LangSwitch className="ml-2" />
        </nav>
      </header>

      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-8 md:pt-14 pb-10 grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] items-start">
        <div className="max-w-[34rem]">
          <h1 className="text-[2rem] md:text-[2.6rem] leading-[1.1] font-semibold text-ink">{t("İstinat duvarı hesabı, kesitten rapora.")}</h1>
          <p className="mt-4 text-[17px] leading-relaxed text-ink/80">{t("Taş ağırlık, betonarme konsol ve payandalı duvarlar için TBDY 2018, Eurocode 7 ve ABD pratiğine göre devrilme, kayma, taşıma gücü ve kesit tasarımı; metraj, keşif ve kurumlara sunulabilir rapor. Girdiyi değiştirdiğiniz anda kesit ve hükümler yenilenir.")}</p>
          {error ? <p role="alert" className="mt-4 card border-bad/40 bg-bad/5 text-bad p-3 text-sm">{error}</p> : null}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Button className="!px-4 !py-2.5 !text-[15px]" onClick={() => setShowForm(true)}>{t("Yeni proje")}</Button>
            <label className="btn-ghost !px-4 !py-2.5 !text-[15px] border border-rule cursor-pointer">
              {t("Dosyadan aç")}
              <input ref={fileRef} type="file" accept=".json,application/json" className="sr-only" aria-label={t("Proje Aç")} onChange={(e) => void onFile(e.target.files?.[0])} />
            </label>
            <Button variant="ghost" className="!px-4 !py-2.5 !text-[15px]" onClick={tryShowcase}>{t("Örnek projeyle dene")}</Button>
          </div>
          <p className="mt-3 text-sm text-stone">{t("Hesaplar tarayıcınızda yapılır")}{status === "guest" ? t("; giriş yaparsanız projeleriniz tüm cihazlarınızda açılır.") : t("; projeler bu cihazda saklanır.")}</p>
          {showForm ? <div className="mt-6"><NewProjectForm onCancel={() => setShowForm(false)} onCreate={(info, seismic) => { useCadUi.getState().setLayout("model"); create(info, seismic); }} onCreateSmart={(info, seismic, start) => { useCadUi.getState().setLayout("design"); create(info, seismic, start); }} /></div> : null}
        </div>
        <HeroSheet />
      </section>

      <section className="border-t border-rule bg-sheet">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 grid gap-8 md:grid-cols-3">
          {FEATURES.map(([h, p]) => (
            <div key={h} className="border-t-2 border-ink pt-3">
              <h2 className="text-base font-semibold text-ink">{h}</h2>
              <p className="mt-2 text-sm leading-relaxed text-ink/75">{p}</p>
            </div>
          ))}
        </div>
      </section>

      <Showcase hasApi={status !== "no-api"} />

      <section className="max-w-6xl mx-auto px-5 md:px-8 py-10 grid gap-8">
        <div aria-label={t("Son projeler")}>
          <h2 className="text-lg font-semibold text-ink">{t("Son projeler")}</h2>
          <div className="mt-3"><RecentProjects items={local} onOpen={openLocal} onDelete={removeLocal} /></div>
        </div>
      </section>

      <footer className="border-t border-rule">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 text-xs text-stone flex flex-wrap items-center gap-x-4 gap-y-2">
          <span>{t("TaşDuvar")} · {t("Hesap motoru v")}{ENGINE_VERSION}</span>
          <Button variant="ghost" className="!p-0 !text-xs" onClick={refresh}>{t("Yenile")}</Button>
          {status !== "no-api" ? <><Link to="/planlar" className="hover:text-ink">{t("Planlar")}</Link><Link to="/gizlilik" className="hover:text-ink">{t("Gizlilik")}</Link><Link to="/kullanim-sartlari" className="hover:text-ink">{t("Kullanım şartları")}</Link><Link to="/on-bilgilendirme" className="hover:text-ink">{t("Ön bilgilendirme")}</Link><Link to="/mesafeli-satis" className="hover:text-ink">{t("Mesafeli satış")}</Link></> : null}
          <Link to="/kilavuz" className="hover:text-ink">{t("Kullanım kılavuzu")}</Link>
        </div>
      </footer>
    </main>
  );
}
